import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { certName } from '../../../utils/certifications';

const Stars = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <svg key={n} viewBox="0 0 16 16" className={`w-3.5 h-3.5 ${n <= Math.round(rating ?? 0) ? 'text-amber-400' : 'text-border'}`} fill="currentColor">
        <path d="M8 1.5l1.75 3.5 3.875.563-2.813 2.737.664 3.863L8 10.25l-3.476 1.913.664-3.863L2.375 5.563 6.25 5z" />
      </svg>
    ))}
    <span className="ml-1 text-xs font-semibold text-foreground">{(rating ?? 0).toFixed(1)}</span>
  </div>
);

const ExpertCard = ({ expert, onViewProfile, onRequestQuote, onMessage }) => {
  const unavailable = expert?.availability === 'unavailable';

  return (
    <article className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group">
      {/* Top accent bar based on verified status */}
      {expert?.verified && (
        <div className="h-0.5 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
      )}

      <div className="p-5 flex flex-col gap-4 flex-1">
        {/* Header row */}
        <div className="flex gap-3.5">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <Image
              src={expert?.avatar}
              alt={expert?.avatarAlt || expert?.name}
              className="w-14 h-14 rounded-xl object-cover ring-2 ring-border group-hover:ring-primary/30 transition-all"
            />
            {expert?.verified && (
              <div className="absolute -bottom-1.5 -right-1.5 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-card">
                <Icon name="Check" size={9} color="#fff" />
              </div>
            )}
          </div>

          {/* Name / title / availability */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-0.5">
              <h3 className="font-bold text-foreground text-sm leading-snug line-clamp-1">{expert?.name}</h3>
              <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide uppercase ${
                unavailable
                  ? 'bg-muted text-muted-foreground'
                  : expert?.availability === 'limited'
                  ? 'bg-amber-400/15 text-amber-600'
                  : 'bg-emerald-500/10 text-emerald-600'
              }`}>
                {unavailable ? 'Busy' : expert?.availability === 'limited' ? 'Limited' : 'Available'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{expert?.title}</p>
            <Stars rating={expert?.rating} />
          </div>
        </div>

        {/* Specialties */}
        {expert?.specialties?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {expert.specialties.slice(0, 3).map((s, i) => (
              <span key={i} className="text-[11px] font-medium px-2.5 py-0.5 bg-primary/8 text-primary rounded-full">
                {s}
              </span>
            ))}
            {expert.specialties.length > 3 && (
              <span className="text-[11px] px-2.5 py-0.5 bg-muted text-muted-foreground rounded-full">
                +{expert.specialties.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 divide-x divide-border border border-border rounded-xl overflow-hidden">
          <div className="py-2.5 text-center">
            <div className="text-base font-bold text-foreground">${expert?.hourlyRate ?? '—'}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">per hour</div>
          </div>
          <div className="py-2.5 text-center">
            <div className="text-base font-bold text-foreground">{expert?.experienceYears ?? '—'}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">yrs exp</div>
          </div>
          <div className="py-2.5 text-center">
            <div className="text-base font-bold text-foreground">{expert?.casesCompleted ?? '—'}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">cases</div>
          </div>
        </div>

        {/* Certifications */}
        {expert?.certifications?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {expert.certifications.slice(0, 3).map((c, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 border border-border rounded text-muted-foreground flex items-center gap-1">
                <Icon name="Award" size={9} className="text-primary flex-shrink-0" />
                {certName(c)}
              </span>
            ))}
            {expert.certifications.length > 3 && (
              <span className="text-[11px] px-2 py-0.5 border border-border rounded text-muted-foreground">
                +{expert.certifications.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Work experience — most recent 2 entries */}
        {expert?.workExperiences?.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">Experience</p>
            <div className="space-y-1.5">
              {expert.workExperiences.slice(0, 2).map((we, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <Icon name="Building2" size={11} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                  <span className="flex-1 min-w-0 truncate">
                    <span className="font-medium text-foreground">{we.role}</span>
                    <span className="text-muted-foreground"> · {we.company}</span>
                  </span>
                  <span className="text-muted-foreground flex-shrink-0 ml-1">{we.start_year}–{we.end_year ?? 'Now'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 mt-auto pt-1">
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs"
            onClick={() => onViewProfile(expert)}
          >
            View Profile
          </Button>
          {(onMessage || onRequestQuote) && (
            <div className="flex gap-2">
              {onMessage && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => onMessage(expert)}
                  iconName="MessageSquare"
                  iconPosition="left"
                >
                  Message
                </Button>
              )}
              {onRequestQuote && (
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1 text-xs"
                  disabled={unavailable}
                  onClick={() => onRequestQuote(expert)}
                >
                  Send Offer
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default ExpertCard;
